/**
 * File-level rejection — the file was never parsed into rows at all.
 * 'readError' covers a rejected FileReader read (disk/permission issues,
 * etc.) rather than a problem with the file's actual content. 'unreadableRows'
 * is now only a defensive fallback for an unterminated quote
 * `parseBidSheetCsv` couldn't isolate to its own row — the common case (a
 * single malformed row) surfaces per-row instead, as a `ParsedBidSheetRow`
 * with `unreadable: true` that validates to `BidSheetRowErrorCode`
 * 'unreadable', so the rest of the file still reaches the review screen.
 */
export type BidSheetFileErrorCode =
  | 'notCsv'
  | 'missingColumns'
  | 'empty'
  | 'readError'
  | 'unreadableRows'

/**
 * A single row read from the CSV, keyed by the header names produced by the
 * List View export (BP-2572): "Listing ID" and "Bid Submission". Neither
 * field is validated yet at this stage.
 */
export type ParsedBidSheetRow = {
  /** 1-based position among data rows (header excluded), for user-facing reference. */
  rowNumber: number
  listingId: string
  bidSubmissionRaw: string
  /**
   * Raw text from the Per-Unit Bid column, for a manifest line on a mobile
   * storefront's sheet — blank for an ordinary listing-level row, and for a
   * manifest line the buyer left untouched. Multiple rows can share the same
   * `listingId` here (a lot row plus its manifest lines); see
   * `groupBidSheetRowsByLot`, which rolls these up into that lot's total.
   * This is a single-unit price — `groupBidSheetRowsByLot` multiplies it by
   * `unitsRaw` before summing, matching how a manifest's own Unit Price /
   * Ext. Price columns relate elsewhere in this domain.
   */
  perUnitBidRaw: string
  /**
   * Raw text from the Units (quantity) column for this row — the multiplier
   * `perUnitBidRaw` is priced against. Blank for an ordinary listing-level
   * row.
   */
  unitsRaw: string
  /**
   * The full CSV row as Papa.parse read it, keyed by header text — every
   * column the buyer's file had, including ones this app never reads
   * itself. Kept so the After-Upload Report (BP-2575) can echo the buyer's
   * own "original" columns back verbatim instead of trying to reconstruct
   * whatever the List View grid's columns happened to be at upload time.
   */
  rawRow: Record<string, string>
  /**
   * Set only on a row whose physical line had an unclosed quote that
   * `parseBidSheetCsv` had to isolate — every other field above is left
   * blank rather than whatever papaparse salvaged from the line after its
   * quote marks were stripped, since none of it is trustworthy. Routes the
   * row straight to `BidSheetRowErrorCode` 'unreadable' in
   * `validateBidSheetRows`, ahead of the normal blank-Listing-ID check.
   * Absent (not `false`) on every ordinary row.
   */
  unreadable?: true
}

export type BidSheetRowErrorCode =
  | 'missingListingId'
  | 'duplicate'
  | 'notFound'
  | 'unacceptedTerms'
  | 'makeAnOffer'
  | 'contract'
  | 'noActiveAuction'
  | 'ended'
  | 'alreadyWon'
  | 'invalidAmount'
  | 'belowMinimum'
  | 'unreadable'

/**
 * Display-only listing info for the results table (Title / Lot # / Min
 * Required Bid columns). Only available once a listing was actually found —
 * `null` only for 'missingListingId', 'notFound' and 'unreadable' rows,
 * which never resolve one. A 'duplicate' or 'noActiveAuction' row still
 * carries it, since the listing it names was found; it's just not the one
 * that gets the bid. `title` prefers the listing's device model (only
 * populated for mobile/device listings) and falls back to its general
 * title otherwise — every listing has a title, not every listing has a model.
 */
export type BidSheetListingDetails = {
  title: string
  lotNumber: string | null
  minimumRequiredBid: number
  currency: string
  /** Drives the Seller Collects tag beside the title in the results table. */
  sellerCollects: boolean
  /** Names the seller in that tag's explainer. */
  storefrontName: string
}

export type ValidatedBidSheetRow = ParsedBidSheetRow &
  (
    | {
        status: 'valid'
        amount: number
        listingDetails: BidSheetListingDetails
        /** Sum of this lot's per-unit bids, or `null` when it has none (an
         * ordinary, non-manifest row, or a manifest lot with every line left
         * blank). Present even when a lot-level bid won — see
         * `usedLotBidOverPerUnit`. */
        perUnitTotal: number | null
        /** True when this lot had both an explicit lot-level bid and a
         * positive per-unit total — the lot-level bid is `amount`; this just
         * flags that a per-unit total was also present and overridden. */
        usedLotBidOverPerUnit: boolean
      }
    | {
        status: 'error'
        errorCode: BidSheetRowErrorCode
        listingDetails: BidSheetListingDetails | null
        /** True when this row's own error doesn't matter because its lot
         * already resolved to a winning bid elsewhere in the group — a
         * conflicting sibling the buyer cleared (not removed), left in the
         * results so it's never silently dropped mid-edit, but excluded from
         * every error tally (Submit gating, the placed step, the
         * After-Upload Report) since the lot it belongs to is already
         * settled. Always `false` for a row whose error is a genuine,
         * unresolved problem. */
        supersededLot: boolean
      }
  )

/**
 * The single predicate every consumer must use to recognize a superseded
 * row (Submit gating, the placed step, `getPlacedRowDisplay`, the
 * After-Upload Report, the review table's own badge) — all of them need to
 * agree on exactly the same rows, so a new consumer should import this
 * instead of re-deriving `status === 'error' && supersededLot` on its own.
 */
export const isSupersededRow = (row: ValidatedBidSheetRow): boolean =>
  row.status === 'error' && row.supersededLot
